import { getRouteApi, Link, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Fab from '@mui/material/Fab';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import AddIcon from '@mui/icons-material/Add';

import { FormattedMessage } from 'react-intl';
import ContentView from '~/components/ContentView';
import DrawerButton from '~/containers/DrawerButton';
import ListItemButton from '@mui/material/ListItemButton';
import { useTheme } from '@mui/material';
import { useCircuits } from '~/hooks/api/useCircuits';
import CircuitDeleteButton from '~/components/CircuitDeleteButton';
import PaginationControlsWithQuery from '~/components/PaginationControls';
import messages from './messages';

const route = getRouteApi('/circuits/');

export const CircuitList = () => {
  const navigate = useNavigate();
  const search = route.useSearch();
  const theme = useTheme();
  const [page, setPage] = useState(search.page ?? 0);
  const [perPage, setPerPage] = useState(20);

  const { data: circuits, isLoading } = useCircuits({
    pageNumber: page + 1,
    pageSize: perPage,
  });

  const circuitsOnCurrentPage = circuits?.page?.items ?? [];
  const totalCount = circuits?.page?.totalItemCount ?? 0;

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    navigate({
      to: '/circuits',
      search: { page: newPage },
    });
  };

  return (
    <ContentView
      header={
        <AppBar position="static">
          <Toolbar>
            <DrawerButton />
            <Typography
              variant="h6"
              style={{ marginLeft: theme.spacing(2) }}
              sx={{
                color: 'inherit',
              }}
            >
              <FormattedMessage {...messages.title} />
            </Typography>
          </Toolbar>
        </AppBar>
      }
      floatingActionButton={
        <Fab component={Link} to="/circuits/new" color="secondary">
          <AddIcon data-testid="create-circuit" />
        </Fab>
      }
      isLoading={isLoading}
    >
      {!isLoading && circuitsOnCurrentPage.length === 0 ? (
        <ListItem data-testid="empty-list-item">
          <Typography color="textSecondary">
            <FormattedMessage {...messages.noCircuitsYet} />
          </Typography>
        </ListItem>
      ) : (
        <>
          <PaginationControlsWithQuery
            totalCount={totalCount}
            isLoading={isLoading}
            initialPage={page}
            perPage={perPage}
            onChangePage={handlePageChange}
            onChangeRowsPerPage={setPerPage}
          />
          <List>
            {circuitsOnCurrentPage.map((circuit) =>
              circuit.id ? (
                <ListItem
                  key={circuit.id}
                  secondaryAction={
                    <CircuitDeleteButton circuitId={circuit.id} />
                  }
                  disablePadding
                >
                  <ListItemButton
                    component={Link}
                    to={`/circuits/${circuit.id}`}
                  >
                    <ListItemText
                      primary={circuit.name}
                      secondary={<FormattedMessage {...messages.name} />}
                    />
                  </ListItemButton>
                </ListItem>
              ) : null
            )}
          </List>
        </>
      )}
    </ContentView>
  );
};

export default CircuitList;
